import { Table } from "@tanstack/react-table";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

import { Meta } from "@/@types/paginated";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useEffect, useState } from "react";
import { useUserFilterActions } from "@/pages/users/ducks/filter/hooks/actions";
import { useFilterState } from "@/pages/users/ducks/filter/hooks/filterState";

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  meta: Meta;
}

export function DataTablePagination<TData>({
  table,
  meta,
}: DataTablePaginationProps<TData>) {
  const filterState = useFilterState();
  const { updateFilter } = useUserFilterActions();
  const { filters } = filterState || { filters: {} };
  const [perPage, setPerPage] = useState(10);

  useEffect(() => {
    table.setPageSize(perPage);
  }, [perPage, table]);

  return (
    <div className="flex flex-col space-y-4 px-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
      <div className="flex-1 text-sm text-muted-foreground text-center sm:text-left">
        {table.getFilteredRowModel().rows.length} linha(s) encontrada(s).
      </div>
      <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-6 lg:space-x-8">
        <div className="flex items-center justify-center space-x-2 sm:justify-start">
          <p className="text-sm font-medium">Linhas por página</p>
          <Select
            value={`${meta.perPage}`}
            onValueChange={(value) => {
              const newPerPage = Number(value);
              const newLastPage = Math.ceil(meta.total / newPerPage);
              const newCurrentPage = Math.min(filters.meta.page, newLastPage);

              updateFilter({
                key: "meta",
                value: {
                  ...filters.meta,
                  perPage: newPerPage,
                  page: newCurrentPage,
                },
              });

              setPerPage(newPerPage);
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={meta.perPage} />
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-center text-sm font-medium sm:w-[100px]">
          Página {meta.currentPage} de {meta.lastPage}
        </div>
        <div className="flex items-center justify-center space-x-2 sm:justify-start">
          <Button
            variant="outline"
            className="h-8 w-8 p-0 sm:flex lg:flex"
            onClick={() => {
              updateFilter({
                key: "meta",
                value: {
                  ...filters.meta,
                  page: 1,
                  perPage: perPage,
                },
              });
            }}
            disabled={meta.prev === null}
          >
            <span className="sr-only">Ir para a primeira página</span>
            <ChevronsLeft />
          </Button>

          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => {
              updateFilter({
                key: "meta",
                value: {
                  ...filters.meta,
                  page: filters.meta.page - 1 || 1,
                },
              });
            }}
            disabled={meta.prev === null}
          >
            <span className="sr-only">Ir para a página anterior</span>
            <ChevronLeft />
          </Button>

          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => {
              updateFilter({
                key: "meta",
                value: {
                  ...filters.meta,
                  page: filters.meta.page + 1 || 1,
                },
              });
            }}
            disabled={meta.next === null}
          >
            <span className="sr-only">Ir para a próxima página</span>
            <ChevronRight />
          </Button>

          <Button
            variant="outline"
            className="h-8 w-8 p-0 sm:flex lg:flex"
            onClick={() => {
              updateFilter({
                key: "meta",
                value: {
                  ...filters.meta,
                  page: meta.lastPage,
                  perPage: perPage,
                },
              });
            }}
            disabled={meta.next === null}
          >
            <span className="sr-only">Ir para a última página</span>
            <ChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
